/**
 * Permission Utility Functions
 * 
 * Simple rule: DB stored permissions are the ONLY source of truth.
 * No fallback magic, no role defaults at runtime.
 */

/**
 * Check if user has a specific permission for a module
 * 
 * Logic:
 * 1. Admin → allow all
 * 2. Module in stored module_permissions → use it
 * 3. Module in stored permissions (legacy) → use it
 * 4. User has ANY stored permissions but module not found → deny
 * 5. User has ZERO stored permissions → allow all (legacy user, backwards compat)
 */
export const hasPermission = (user, module, action) => {
  if (!user) return false;
  
  // Admin has all permissions
  if (user.role === 'Admin') return true;
  
  // Check stored module_permissions (primary)
  if (user.module_permissions && user.module_permissions[module]) {
    return user.module_permissions[module][action] === true;
  }
  
  // Fallback to legacy permissions structure
  if (user.permissions && user.permissions[module]) {
    return user.permissions[module][action] === true;
  }
  
  // If user has ANY configured permissions but this module isn't listed → deny
  const hasConfiguredPermissions = 
    (user.module_permissions && Object.keys(user.module_permissions).length > 0) ||
    (user.permissions && Object.keys(user.permissions).length > 0);
  if (hasConfiguredPermissions) {
    return false;
  }
  
  // No permissions configured at all → legacy user, allow by default
  return true;
};

export const canView = (user, module) => hasPermission(user, module, 'view');
export const canCreate = (user, module) => hasPermission(user, module, 'create');
export const canEdit = (user, module) => hasPermission(user, module, 'edit');
export const canDelete = (user, module) => hasPermission(user, module, 'delete');

export const hasRole = (user, roles) => {
  if (!user || !roles) return false;
  return roles.includes(user.role);
};

/**
 * Check navigation item visibility
 */
export const canAccessNavItem = (user, navItem) => {
  if (!user) return false;
  if (user.role === 'Admin') return true;
  
  // Special: Quality Analytics — visible if user has ANY QA tab permission
  if (navItem.permission === 'qualityAnalytics.any') {
    const hasAnyConfig = 
      (user.module_permissions && Object.keys(user.module_permissions).length > 0) ||
      (user.permissions && Object.keys(user.permissions).length > 0);
    if (!hasAnyConfig) return true; // legacy user → full access
    const qaPerm = user.module_permissions?.qualityAnalytics || user.permissions?.qualityAnalytics || {};
    return Object.values(qaPerm).some(v => v === true);
  }
  
  // If item has permission requirement, check it
  if (navItem.permission) {
    const [module, action] = navItem.permission.split('.');
    return hasPermission(user, module, action);
  }
  
  // If item has role requirement, check it
  if (navItem.roles) {
    return hasRole(user, navItem.roles);
  }
  
  // No restrictions
  return true;
};

/**
 * Module name mappings for route permission checks
 */
export const MODULE_PERMISSIONS = {
  '/': 'dashboard',
  '/daily-production-entry': 'dailyProduction',
  '/grn': 'grn',
  '/drawing-management': 'drawingManagement',
  '/quality-gate-inspection': 'qualityGate',
  '/quality-gate-reports': 'qualityGate',
  '/inline-quality': 'inlineQuality',
  '/jobs': 'jobCards',
  '/jobs/create': 'jobCards',
  '/shift-reports': 'shiftReports',
  '/dispatch-reports': 'dispatchReports',
  '/wip-inventory': 'wipInventory',
  '/raw-material-inventory': 'rawMaterialInventory',
  '/machines': 'machines',
  '/users': 'userManagement',
  '/supervisor-assignments': 'supervisorManagement',
  '/employees': 'employees',
  '/maintenance': 'breakdowns',
  '/maintenance/breakdowns': 'breakdowns',
  '/maintenance/report-breakdown': 'breakdowns',
  '/maintenance/machine-history': 'machineHistory',
  '/maintenance/analytics': 'maintenanceAnalytics',
  '/calibration': 'calibration',
  '/calibration/gauges': 'calibration',
  '/calibration/gauges/new': 'calibration',
  '/calibration/schedule': 'calibration',
  '/calibration/my-gauges': 'calibration',
  '/calibration/settings': 'calibration',
};

export const getRoutePermission = (path) => {
  if (MODULE_PERMISSIONS[path]) {
    return MODULE_PERMISSIONS[path];
  }
  // Prefix match — skip '/' to prevent it from matching every route
  // Sort by key length descending so more specific routes match first
  const entries = Object.entries(MODULE_PERMISSIONS)
    .filter(([key]) => key !== '/')
    .sort((a, b) => b[0].length - a[0].length);
  for (const [key, module] of entries) {
    if (path.startsWith(key + '/') || path === key) {
      return module;
    }
  }
  // Check root path last
  if (path === '/') return MODULE_PERMISSIONS['/'] || null;
  return null;
};
